import React, { useState, useEffect, useRef } from "react"
import cx from "classnames"
import LazyLoad from "react-lazy-load"
import { isMobile, isTablet } from "react-device-detect"
import _get from "lodash/get"
import { scroller } from "react-scroll"
import { useDispatch, useSelector } from "react-redux"
import _isEmpty from "lodash/isEmpty"
import {
  searchState,
  setSearchFilter,
  setSearchKeyword,
} from "@/store/features/searchSlice"

import {
  getProducts,
  selectProductListState,
} from "@/store/features/productListSlice"

import { selectAuthState } from "@/store/features/authSlice"
import usePrevious from "@/hooks/usePrevious"
import {
  scrollToProduct,
  scrollToFirstProduct,
} from "@/components/Search/v1/ProductsTab/helper"

import { getConfig } from "@/constants/config"
import {
  getUserPersona,
  replaceSearchUrl,
  retainSearchQueryParams,
  getSortItemBasedOnURL,
  getAllFilterQueries as getFacetsFromUrl,
} from "@/utils/helper"
import useWindowResize from "@/hooks/useWindowResize"

import TechnicalTile from "@/components/Search/v1/TechnicalTile"
import Filter from "@/components/ProductList/v2/Filter"
import Pagination from "@/components/ProductList/v2/ProductListView/Pagination"
import StickyNavHeader from "@/components/ProductList/v2/ProductListView/StickyNav/stickyNavHeader"
import styles from "@/components/ProductList/v2/ProductListView/index.module.scss"
import sortStyles from "@/components/ProductList/v2/Sort/index.module.scss"
import filterStyles from "@/components/ProductList/v2/Filter/index.module.scss"

const TechnicalTab = props => {
  const {
    totalResults = 0,
    searchKeyword = "",
    displayPrint,
    displayShare,
    page = "",
    staticTexts = {},
    onSearch = () => {},
    productsPerPage,
    type = "",
    marketParamValues = [],
  } = props

  const [width] = useWindowResize()
  const dispatch = useDispatch()
  const { isLandscape } = props
  const [showFilter, setShowFilter] = useState(width >= 1024 ? true : false)
  const [rowCols, setRowCols] = useState(width < 1024 ? 1 : 3)
  const [curRows, setCurRows] = useState(30)
  const [start, setStart] = useState(0)
  const filterModalWindow = useRef(null)
  const [selectedSort, setSelectedSort] = useState({})
  const [filterQueries, setFilterQueries] = useState([])
  const [siteName, setSiteName] = useState()
  const [dataPopulated, setDataPopulated] = useState(false)
  const [scrollToSku, setScrollToSku] = useState(
    sessionStorage?.plpScrollSku ?? ""
  )
  const [isFusionLoaded, setIsFusionLoaded] = useState(false)
  const prevRows = usePrevious(curRows)
  const [isLoadMore, setIsLoadMore] = useState(false)

  const {
    searchDetails = {},
    searchResponse: { data: { fusion: searchFusion = {} } = {} } = {},
  } = useSelector(searchState)

  const {
    data = [],
    total,
    facets = {},
    facetLabels,
    colors,
  } = useSelector(selectProductListState).plp

  const { access_token: accessToken } = useSelector(selectAuthState)

  const retainedFilters =
    searchDetails.keyword === searchKeyword && searchDetails[type]?.filters
      ? searchDetails[type].filters
      : []

  useEffect(() => {
    getConfig().then(async config => {
      const brandName = _get(config, "general.siteName")
      setSiteName(brandName)
    })
    const selectedSortItem = getSortItemBasedOnURL(staticTexts, {
      name: "Featured",
    })
    setSelectedSort(selectedSortItem)
  }, [])

  const filterClass = cx({
    "product-listing__filter": true,
    "product-listing__filter--hide":
      (!isTablet && !isMobile) || width > 992 ? !showFilter : false,
    "product-listing__filter--modal":
      isTablet || isMobile || width < 992 ? showFilter : false,
  })

  const tileClass = cx({
    "product-listing__tile-col": true,
    "product-listing__tile-col--full-width":
      (!isTablet && !isMobile) || width > 992 ? !showFilter : true,
  })

  const sorts = [
    { name: staticTexts?.featured },
    { name: staticTexts?.atoz, query: "productName_s+asc" },
    { name: staticTexts?.ztoa, query: "productName_s+desc" },
  ]

  const executeScroll = () => {
    scroller.scrollTo("products-tab", {
      duration: 1500,
      delay: 0,
      smooth: "easeInOutQuart",
    })
  }

  useEffect(() => {
    isTablet
      ? window.outerWidth >= 1024
        ? setRowCols(3)
        : setRowCols(2)
      : isMobile
      ? isLandscape
        ? setRowCols(2)
        : setRowCols(2)
      : setRowCols(3)
  }, [isLandscape, width])

  useEffect(() => {
    if (accessToken && filterQueries != null) {
      // will trigger from onLoadMore
      if (start) {
        generateListingUrl(isLoadMore, false)
        // Will trigger when user coming back from PDP page
      } else if (start == 0 && scrollToSku) {
        setScrollToSku("")
        generateListingUrl(false, true)
      }
      // When downgrade pagination
      else if (start == 0 && curRows && !!prevRows && prevRows > curRows) {
        generateListingUrl(false, true)
      }
      // will trigger from onRows. And rest of the dependencies
      else if (start == 0 && curRows) {
        generateListingUrl(false, false)
      }
    }
  }, [accessToken, start, curRows, selectedSort, filterQueries])

  useEffect(() => {
    switch (true) {
      case width > 1024:
        setRowCols(1)
        break
      case width < 668:
        setRowCols(1)
        break
      default:
        setRowCols(1)
    }
  }, [isLandscape, width])

  useEffect(() => {
    // To scrollup the product list on back
    if (dataPopulated) scrollToProduct()
  }, [dataPopulated])

  useEffect(() => {
    if (showFilter && (width < 992 || isMobile || isTablet)) {
      filterModalWindow?.current?.focus()
    } else if (!showFilter && (width < 992 || isMobile || isTablet)) {
      document.getElementById("showFilter")?.focus()
    }
  }, [showFilter])

  useEffect(() => {
    if (!_isEmpty(searchFusion) && accessToken && !isFusionLoaded) {
      setIsFusionLoaded(true)
      if (retainedFilters.length > 0) {
        setFilterQueries(retainedFilters)
      } else {
        const filterQuery = getFacetsFromUrl(
          { attributeName: "" }, // this arg to avoid inner condition
          searchFusion,
          marketParamValues
        )
        setFilterQueries(filterQuery)
        dispatch(setSearchKeyword(searchKeyword))
        dispatch(setSearchFilter({ queries: filterQuery, key: type }))
        retainSearchQueryParams(type)
      }
    }
  }, [searchFusion, accessToken])

  const onRows = rows => {
    setStart(0)
    setCurRows(rows)
  }

  const onLoadMore = () => {
    setIsLoadMore(true)
    setStart(start + curRows)
  }

  const onSort = query => {
    setStart(0)
    replaceSearchUrl("sort", query?.query)
    replaceSearchUrl("tab", type)
    retainSearchQueryParams(type)

    setSelectedSort(query)
  }

  const generateListingUrl = (update, enableScroll = true, queries) => {
    let callStart = 0
    const persona = getUserPersona()
    if (!queries && retainedFilters.length > 0) {
      setFilterQueries(retainedFilters)
      queries = retainedFilters
    } else {
      queries = filterQueries
      callStart = start
      dispatch(setSearchKeyword(searchKeyword))
      dispatch(setSearchFilter({ queries: filterQueries, key: type }))
    }

    const fq = ['Technical:("Yes")']
    queries?.forEach(q =>
      fq.push(`${q.facet}:("${encodeURIComponent(q.value.join('","'))}")`)
    )

    const query = {
      fq,
      rows: curRows,
      start: callStart,
    }
    if (selectedSort.name !== staticTexts.featured) {
      if (selectedSort.name) {
        query.sort = selectedSort.query
      }
    }
    const fl = [
      "id",
      "slug_s",
      "masterSKU_s",
      "productName_s",
      "Product_Category",
      "productImages.url_ss",
      "ProductSustainability_s",
      "ProductBrandName_s",
      "ProductADACompliant_s",
      "ProductBrandNameDisplay_s",
      "ProductDescriptionProductShort_s",
      "ProductResource.DXFPlanView.resourceFullWebURL_ss",
      "ProductResource.DWGPlanView.resourceFullWebURL_ss",
      "ProductResource.DXFFrontView.resourceFullWebURL_ss",
      "ProductResource.DWGFrontView.resourceFullWebURL_ss",
      "ProductResource.DXFSideView.resourceFullWebURL_ss",
      "ProductResource.DWGSideView.resourceFullWebURL_ss",
      "ProductResource.3DDXFSymbol.resourceFullWebURL_ss",
      "ProductResource.3DDWGSymbol.resourceFullWebURL_ss",
      "ProductResource.3DRevit.resourceFullWebURL_ss",
      "ProductResource.3D3DS.resourceFullWebURL_ss",
      "ProductResource.3DSketchup.resourceFullWebURL_ss",
      "ProductResource.CutOutDXF.resourceFullWebURL_ss",
      "ProductResource.3DOBJ.resourceFullWebURL_ss",
      "ProductResource.SpecPDFFileName.resourceFullWebURL_ss",
      "ProductResource.SpecPDFFileName.resourceTitle_ss",
      "ProductResource.EnvironProdDecl.resourceFullWebURL_ss",
      "ProductResource.EnvironProdDecl.resourceTitle_ss",
      "ProductResource.HomeownersGuide.resourceFullWebURL_ss",
      "ProductResource.HomeownersGuide.resourceTitle_ss",
      "ProductResource.InstallationWithoutSPPDF.resourceFullWebURL_ss",
      "ProductResource.InstallationWithoutSPPDF.resourceTitle_ss",
      "ProductIsRetail",
      "comboSKUAvailable_b",
      "ProductProductNo_s",
      "category1_s",
      `priceList.${persona}.finalPrice_d`,
      `priceList.${persona}.discountedPrice_d`,
      `priceList.${persona}.saleOffer_s`,
      `priceStartingAt.${persona}_d`,
      `discountedPriceStartingAt.${persona}_d`,
      "Tile_Title",
    ]

    const queryParams = {
      q: searchKeyword,
      fl: fl.join(","),
      ...query,
      displaytype: "search",
      update,
    }
    if (enableScroll) setDataPopulated(false)

    dispatch(getProducts({ ...queryParams }))
      .then(() => {
        if (enableScroll) setDataPopulated(true)
      })
      .catch(() => {
        if (enableScroll) setDataPopulated(false)
      })
  }

  const handleKeyPress = e => {
    if (width < 992) {
      const focusElements =
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), button:not([disabled]) [role="button]:not([hidden=true])'
      const focusContent =
        filterModalWindow?.current?.querySelectorAll(focusElements)
      const firstElem = focusContent[0]
      const lastElem = focusContent[focusContent.length - 1].disabled
        ? focusContent[focusContent.length - 2]
        : focusContent[focusContent.length - 1]
      if (e.key === "Escape") {
        setShowFilter(false)
      }
      if (e.key === "Tab") {
        if (
          e.shiftKey &&
          (document.activeElement === firstElem ||
            document.activeElement === filterModalWindow.current)
        ) {
          e.preventDefault()
          lastElem.focus()
        }
        if (!e.shiftKey && document.activeElement === lastElem) {
          e.preventDefault()
          firstElem.focus()
        }
      }
    }
  }
  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" })
    document.getElementById(`kf-product-list-tile-details-0`)?.focus()
  }
  const handleFilter = queries => {
    setIsLoadMore(false)
    dispatch(setSearchKeyword(searchKeyword))
    dispatch(setSearchFilter({ queries, key: type }))
    setFilterQueries(queries)
    scrollToFirstProduct()
  }
  return (
    <div
      className={`${styles.productListWrapper} ${sortStyles.productListingSearchWrapper} ${filterStyles.productListingFilterWrapper}`}
    >
      <div className="product-listing productListing_technical">
        <StickyNavHeader
          showFilter={showFilter}
          rowCols={rowCols}
          setShowFilter={setShowFilter}
          setRowCols={setRowCols}
          staticTexts={staticTexts}
          sorts={sorts}
          selectedSort={selectedSort}
          handleSort={onSort}
          totalResults={totalResults}
          isSearchTab={true}
          searchKeyword={searchKeyword}
          onSearch={onSearch}
          page={page}
          type={type}
        />
        {/* product-list */}
        <div className="container kf-react-plp-container product-listing__tiles">
          <div className="product-listing__row d-block">
            <div className="filter-col">
              <div className={filterClass} aria-modal ref={filterModalWindow}>
                <div
                  className={`product-listing__filter--sticky`}
                  aria-hidden={!showFilter}
                  onKeyDown={handleKeyPress}
                >
                  {facets && (
                    <Filter
                      rowCols={rowCols}
                      texts={staticTexts}
                      sort={sorts}
                      selectedSort={selectedSort}
                      onSortChange={onSort}
                      currFilters={filterQueries}
                      selectedFilterCount={
                        filterQueries &&
                        filterQueries.reduce((acc, item) => {
                          return acc + item.value.length
                        }, 0)
                      }
                      onFilter={handleFilter}
                      close={setShowFilter}
                      colors={colors}
                      discontinuedCheck={false}
                      displayShare={true}
                      displayPrint={true}
                      showFilter={showFilter}
                      handleScroll={executeScroll}
                      totalResults={total}
                      showSort={width <= 991}
                      type={type}
                      tabIndex="0"
                      hidePriceRange
                      page={page || ""}
                      facetData={facets}
                      facetLabels={facetLabels}
                      addToUrl={siteName ?? false}
                      isLandscape={isLandscape}
                    />
                  )}
                </div>
              </div>
              <div className={tileClass}>
                <div className={`product-listing__row row-cols-1`} role="list">
                  {data.map((element, i) => (
                    <React.Fragment key={i}>
                      <div className="product-listing__tile" role="listitem">
                        <LazyLoad
                          className="lazyload-wrapper kf-lazyload-wrapper"
                          height="300"
                        >
                          <TechnicalTile
                            page={page}
                            data={element}
                            fullWidth={!showFilter}
                            staticTexts={staticTexts}
                            highlightText={searchKeyword}
                          />
                        </LazyLoad>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                {/* pagination component for Loadmore, ProductPerPage, BackToTop, Print,Social share*/}
                <Pagination
                  isSearchTab={true}
                  staticTexts={staticTexts}
                  displayShare={displayShare}
                  displayPrint={displayPrint}
                  curRows={curRows}
                  productsPerPage={productsPerPage}
                  totalResults={total}
                  onRows={onRows}
                  handleBackToTop={handleBackToTop}
                  width={width}
                  page={page}
                  type={type}
                  data={data}
                  onLoadMore={onLoadMore}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TechnicalTab
